/* eslint-disable react/function-component-definition */
import { useQuery } from "@apollo/client";
import AmericanCompanyContext from "components/company/v3/context/AmericanCompanyContext";
import BrazilianCompanyContext from "components/company/v3/context/BrazilianCompanyContext";
import BrazilianRealEstateFundContext from "components/company/v3/context/BrazilianRealEstateFundContext";
import { platformVerticalToSecurityCountry } from "components/financials/modern/utils/platformVertical";
import LocaleContext from "components/i18n/LocaleContext";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import type { ReactElement, ReactNode } from "react";

type RootIssuerProxyChildrenFnProps = {
  issuer: IIssuerByTickerSymbolQuery["issuerByTickerSymbol"] | null;
  loading: boolean;
};

interface RootIssuerProxyProps {
  issuer: Omit<ICompanyIssuerFieldsFragment, "__typename">;
  children: ReactNode | ((props: RootIssuerProxyChildrenFnProps) => ReactElement);
}
export default function RootIssuerProxy({ issuer, children }: RootIssuerProxyProps) {
  const { data, loading } = useQuery<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>(
    IssuerByTickerSymbolQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        tickerSymbol: issuer.tickerSymbol,
        country: platformVerticalToSecurityCountry(issuer.vertical),
      },
    },
  );

  if (loading) {
    return typeof children === "function" ? children({ loading, issuer: null }) : null;
  }

  if (!data?.issuerByTickerSymbol) {
    return typeof children === "function" ? children({ loading, issuer: null }) : null;
  }

  if (data.issuerByTickerSymbol.__typename === "AmericanCompanyIssuer") {
    return (
      <LocaleContext.Provider value="USA">
        <AmericanCompanyContext.Provider value={data.issuerByTickerSymbol}>
          {typeof children === "function" ? children({ loading, issuer: data.issuerByTickerSymbol }) : children}
        </AmericanCompanyContext.Provider>
      </LocaleContext.Provider>
    );
  }
  if (data.issuerByTickerSymbol.__typename === "BrazilianCompanyIssuer") {
    return (
      <LocaleContext.Provider value="BRA">
        <BrazilianCompanyContext.Provider value={data.issuerByTickerSymbol}>
          {typeof children === "function" ? children({ loading, issuer: data.issuerByTickerSymbol }) : children}
        </BrazilianCompanyContext.Provider>
      </LocaleContext.Provider>
    );
  }
  if (data.issuerByTickerSymbol.__typename === "BrazilianRealEstateFund") {
    return (
      <LocaleContext.Provider value="BRA">
        <BrazilianRealEstateFundContext.Provider value={data.issuerByTickerSymbol}>
          {typeof children === "function" ? children({ loading, issuer: data.issuerByTickerSymbol }) : children}
        </BrazilianRealEstateFundContext.Provider>
      </LocaleContext.Provider>
    );
  }

  return null;
}
